import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { AlertTriangle, Link2 } from 'lucide-react';

import { convertLead, getPartyGroups } from '@/app/services/leadService';
import { useAuthStore } from '@/app/store/authStore';
import { MobileBottomSheet } from '@/components/ui/MobileBottomSheet';
import { MobileButton } from '@/components/ui/MobileButton';
import { MobileInput } from '@/components/ui/MobileInput';
import { cn } from '@/lib/utils';
import type { LeadDetail } from '@/app/types/lead';

interface Props {
    lead: LeadDetail;
    open: boolean;
    onClose: () => void;
}

interface NameClash {
    existingPartyId: number;
    message: string;
}

export function ConvertLeadSheet({ lead, open, onClose }: Props) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const user = useAuthStore((state) => state.user);

    const [partyName, setPartyName] = useState(lead.business_name);
    const [groupId, setGroupId] = useState<number | null>(null);
    const [siteId, setSiteId] = useState<number | null>(lead.site_id ?? user?.sites?.[0]?.id ?? null);
    const [gstin, setGstin] = useState('');
    const [clash, setClash] = useState<NameClash | null>(null);

    const { data: groups } = useQuery({
        queryKey: ['party-groups'],
        queryFn: getPartyGroups,
        enabled: open,
        staleTime: 5 * 60 * 1000,
    });

    useEffect(() => {
        if (open) {
            setPartyName(lead.business_name);
            setGstin('');
            setClash(null);
            setSiteId(lead.site_id ?? user?.sites?.[0]?.id ?? null);
        }
    }, [open, lead.business_name, lead.site_id, user]);

    useEffect(() => {
        // Prefer a sales/customer group; never LEADS (already filtered in the service).
        if (groups?.length && groupId === null) {
            const preferred = groups.find((g) => g.name.toUpperCase().includes('SALES')) ?? groups[0];
            setGroupId(preferred.id);
        }
    }, [groups, groupId]);

    const mutation = useMutation({
        mutationFn: (linkToExisting?: number) =>
            convertLead(lead.id, {
                party_group_id: groupId!,
                site_id: siteId!,
                primary_party_type: 'Customer',
                party_name: partyName.trim() || undefined,
                gstin: gstin.trim() || undefined,
                link_to_existing_party_id: linkToExisting,
            }),
        onSuccess: (result) => {
            queryClient.invalidateQueries({ queryKey: ['lead', lead.id] });
            queryClient.invalidateQueries({ queryKey: ['leads'] });
            queryClient.invalidateQueries({ queryKey: ['lead-summary'] });
            queryClient.invalidateQueries({ queryKey: ['parties'] });
            toast.success(
                result.created_new_party
                    ? `${result.party.name} is now a customer`
                    : `Linked to ${result.party.name}`
            );
            onClose();
            navigate(`/parties/${result.party.id}`);
        },
        onError: (error) => {
            const detail = axios.isAxiosError(error) ? error.response?.data?.detail : null;

            // The important branch: a colleague already added this shop. Offer to
            // link instead of dead-ending, or the rep will save "Shop Name 2" and
            // corrupt the master data permanently.
            if (detail && typeof detail === 'object' && detail.code === 'party_name_exists') {
                setClash({
                    existingPartyId: detail.existing_party_id,
                    message: detail.message ?? 'A party with this name already exists.',
                });
                return;
            }
            if (detail && typeof detail === 'object' && detail.code === 'already_converted') {
                toast.error('This lead was already converted');
                onClose();
                return;
            }
            toast.error(
                typeof detail === 'string' ? detail : 'Could not convert the lead'
            );
        },
    });

    const sites = user?.sites ?? [];
    const canConvert = !!groupId && !!siteId && partyName.trim().length > 0;

    return (
        <MobileBottomSheet open={open} onClose={onClose} title="Convert to customer" heightMode="auto">
            <div className="p-4 space-y-4">
                {clash ? (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3">
                            <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-yellow-400 flex-shrink-0 mt-0.5" />
                                <div className="text-sm min-w-0">
                                    <p className="text-yellow-200 font-medium">Already a customer</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        {clash.message} Link this lead to that customer, or change
                                        the name if it is a different outlet.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <MobileButton
                            className="w-full"
                            icon={<Link2 className="h-4 w-4" />}
                            isLoading={mutation.isPending}
                            onClick={() => mutation.mutate(clash.existingPartyId)}
                        >
                            Link to this customer
                        </MobileButton>

                        <MobileInput
                            label="Or use a different name"
                            value={partyName}
                            onChange={(e) => setPartyName(e.target.value)}
                        />
                        <MobileButton
                            className="w-full"
                            variant="secondary"
                            disabled={!canConvert || mutation.isPending}
                            onClick={() => {
                                setClash(null);
                                mutation.mutate(undefined);
                            }}
                        >
                            Create as a new customer
                        </MobileButton>
                    </div>
                ) : (
                    <>
                        <MobileInput
                            label="Customer name"
                            value={partyName}
                            onChange={(e) => setPartyName(e.target.value)}
                        />

                        <div>
                            <p className="mobile-section-header mb-2">Party group</p>
                            <div className="flex gap-2 flex-wrap">
                                {(groups ?? []).map((group) => (
                                    <button
                                        key={group.id}
                                        onClick={() => setGroupId(group.id)}
                                        className={cn(
                                            'px-3 py-2 rounded-full text-sm font-medium',
                                            groupId === group.id
                                                ? 'bg-primary text-primary-foreground'
                                                : 'bg-secondary text-muted-foreground'
                                        )}
                                    >
                                        {group.name}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {sites.length > 1 && (
                            <div>
                                <p className="mobile-section-header mb-2">Site</p>
                                <div className="flex gap-2 flex-wrap">
                                    {sites.map((site) => (
                                        <button
                                            key={site.id}
                                            onClick={() => setSiteId(site.id)}
                                            className={cn(
                                                'px-3 py-2 rounded-full text-sm font-medium',
                                                siteId === site.id
                                                    ? 'bg-primary text-primary-foreground'
                                                    : 'bg-secondary text-muted-foreground'
                                            )}
                                        >
                                            {site.name}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <MobileInput
                            label="GSTIN (optional)"
                            placeholder="Can be added later"
                            value={gstin}
                            onChange={(e) => setGstin(e.target.value.toUpperCase())}
                        />

                        <p className="text-xs text-muted-foreground">
                            This creates a customer record and its ledger account. The lead stays as
                            history, with its photos and follow-ups.
                        </p>

                        <MobileButton
                            className="w-full"
                            disabled={!canConvert}
                            isLoading={mutation.isPending}
                            onClick={() => mutation.mutate(undefined)}
                        >
                            Convert to customer
                        </MobileButton>
                    </>
                )}
            </div>
        </MobileBottomSheet>
    );
}
